<template>
  <div class="content">
    <div class="row animated fadeIn">
      <div class="col-md-12">

        <!-- Header card -->
        <div class="card mb-3" style="border-radius:12px;border:none;box-shadow:0 2px 10px rgba(0,0,0,0.07);overflow:hidden;">
          <div class="card-body" style="padding:20px 24px;">
            <!-- Top row: back + title + status -->
            <div class="d-flex align-items-center mb-3" style="gap:12px;">
              <button class="btn btn-sm btn-light" style="border:1px solid #dee2e6;border-radius:7px;padding:5px 12px;" @click="$router.push({ path: '/admin/cloudflare', query: { tab: 'provisioning' } })">
                <i class="fa fa-arrow-left mr-1"></i> Back
              </button>
              <div style="flex:1;">
                <div class="d-flex align-items-center" style="gap:10px;">
                  <span style="font-size:18px;font-weight:700;color:#222;">Cloudflare Enterprise Automation</span>
                </div>
                <div v-if="batch" style="font-size:11px;color:#bbb;margin-top:2px;">
                  <i class="fa fa-clock-o mr-1"></i>{{ formatDate(batch.createdAt) }}
                </div>
              </div>
            </div>

          </div>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="text-center py-5">
          <i class="fa fa-spinner fa-spin fa-2x text-muted"></i>
          <div class="text-muted mt-2" style="font-size:13px;">Loading batch...</div>
        </div>

        <!-- Domains list -->
        <div v-else-if="batch">
          <!-- Filters -->
          <div class="d-flex align-items-center mb-3" style="gap:6px;flex-wrap:wrap;background:#f8f9fa;border-radius:10px;padding:6px 8px;border:1px solid #e9ecef;">
            <button
              v-for="f in filters"
              :key="f.value"
              class="filter-btn"
              :class="{ 'filter-btn-active': statusFilter === f.value, ['filter-btn-' + f.value]: true }"
              @click="statusFilter = f.value"
            >
              <span class="filter-btn-label">{{ f.label }}</span>
              <span class="filter-btn-count">{{ f.count }}</span>
            </button>
          </div>

          <div
            v-for="(domain, index) in filteredDomains"
            :key="domain.id"
            class="card mb-2"
            style="border-radius:8px;border:1px solid #e9ecef;box-shadow:0 1px 4px rgba(0,0,0,0.04);overflow:hidden;transition:box-shadow .2s;"
          >
            <!-- Row header -->
            <div
              class="d-flex align-items-center"
              style="cursor:pointer;padding:12px 18px;background:#fff;user-select:none;"
              @click="toggle(domain.id)"
            >
              <!-- Domain name -->
              <div style="flex:1;min-width:0;display:flex;align-items:center;gap:8px;">
                <div style="font-weight:600;font-size:14px;color:#333;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">{{ domain.domain }}</div>
                <button
                  class="btn btn-sm"
                  style="padding:2px 7px;font-size:11px;border:1px solid #dee2e6;border-radius:5px;background:#fff;color:#888;flex-shrink:0;line-height:1.4;"
                  :title="copied === domain.id ? 'Copied!' : 'Copy domain'"
                  @click.stop="copyDomain(domain)"
                >
                  <i class="fa" :class="copied === domain.id ? 'fa-check text-success' : 'fa-copy'"></i>
                </button>
              </div>

              <!-- Status badge -->
              <div style="margin-right:14px;display:flex;gap:5px;align-items:center;">
                <span
                  class="badge badge-pill"
                  :class="statusBadge(domain.status)"
                  style="font-size:11px;padding:4px 10px;"
                >
                  {{ statusLabel(domain.status) }}
                </span>
              </div>

              <!-- Chevron -->
              <i
                class="fa"
                :class="expanded[domain.id] ? 'fa-chevron-up' : 'fa-chevron-down'"
                style="color:#ccc;font-size:12px;flex-shrink:0;"
              ></i>
            </div>

            <!-- Expanded content -->
            <div v-if="expanded[domain.id]" style="border-top:1px solid #f0f0f0;background:#fafbfc;padding:16px 18px;">

              <!-- Prechecks -->
              <div v-if="domain.prechecks" class="mb-3">
                <div class="section-label">Prechecks</div>
                <div style="display:flex;flex-wrap:wrap;gap:8px;">
                  <div
                    v-for="(val, key) in precheckItems(domain.prechecks)"
                    :key="key"
                    style="display:flex;align-items:center;gap:7px;padding:7px 12px;border-radius:7px;border:1px solid;"
                    :style="precheckStyle(key, val)"
                  >
                    <i class="fa" :class="precheckIcon(key, val)" style="font-size:13px;"></i>
                    <div>
                      <div style="font-size:11px;color:#888;line-height:1;">{{ formatGroupName(key) }}</div>
                      <div style="font-size:12px;font-weight:600;margin-top:1px;" :style="{ color: precheckValueColor(key, val) }">
                        {{ formatPrecheckValue(key, val) }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Logs grouped by type -->
              <div v-if="domain.logs && domain.logs.length">
                <div v-for="(group, groupName) in groupedLogs(domain.logs)" :key="groupName" class="mb-3">
                  <div class="section-label" style="display:flex;align-items:center;gap:8px;">
                    <span>{{ formatGroupName(groupName) }}</span>
                    <span style="font-size:10px;background:#e9ecef;color:#666;padding:1px 7px;border-radius:99px;font-weight:600;text-transform:none;letter-spacing:0;">{{ group.length }}</span>
                  </div>
                  <div class="log-terminal">
                    <div v-for="(log, i) in group" :key="i" class="log-line">
                      <span class="log-time">{{ formatTimestamp(log.timestamp) }}</span>
                      <span class="log-msg" :class="'log-' + (log.status || 'default')">{{ log.message }}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="empty-state">
                <i class="fa fa-hourglass-o mr-1"></i> No logs yet.
              </div>

              <!-- Steps from domains collection -->
              <div v-if="stepsLoading[domain.id]" class="mt-3 text-muted" style="font-size:12px;">
                <i class="fa fa-spinner fa-spin mr-1"></i> Loading steps...
              </div>
              <div v-else-if="domainSteps[domain.id]" class="mt-3">
                <div class="section-label">Steps</div>
                <table class="table table-sm" style="font-size:12px;background:#fff;border-radius:6px;overflow:hidden;">
                  <thead class="thead-light">
                    <tr>
                      <th class="text-left">Step</th>
                      <th style="width:80px;">Status</th>
                      <th style="width:60px;"></th>
                    </tr>
                  </thead>
                  <tbody>
                    <template v-for="(step, key) in sortedSteps(domainSteps[domain.id])">
                      <tr :key="key">
                        <td class="text-left">
                          <div class="d-flex align-items-center" style="gap:6px;">
                            {{ step.text }}
                            <span v-if="step.data && step.data.dns_mode" style="font-size:10px;font-weight:600;padding:1px 7px;border-radius:99px;background:#e8f4fd;color:#1a73e8;border:1px solid #c5dff8;">{{ step.data.dns_mode }}</span>
                            <span v-if="step.data && step.data.state === 'skipped' && step.data.messages && step.data.messages[0]" style="font-size:11px;color:#666;font-weight:700;font-style:italic;">— {{ step.data.messages[0] }}</span>
                            <span v-if="step.data && step.data.state === 'aborted' && step.data.errors && step.data.errors[0]" style="font-size:11px;color:#c62828;font-weight:700;font-style:italic;">{{ step.data.errors[0].message }}</span>
                          </div>
                        </td>
                        <td>
                          <i v-if="step.data && step.data.state === 'success'" class="fa fa-check-circle text-success"></i>
                          <i v-else-if="step.data && step.data.state === 'failed'" class="fa fa-times-circle text-danger"></i>
                          <i v-else-if="step.data && (step.data.state === 'skipped' || step.data.state === 'aborted')" class="fa fa-minus-circle" style="color:#aaa;"></i>
                          <i v-else-if="step.data && step.data.state === 'waiting'" class="fa fa-clock-o" style="color:#f9a825;"></i>
                          <i v-else-if="step.status === 'pass'" class="fa fa-check-circle text-success"></i>
                          <i v-else-if="step.status === 'fail'" class="fa fa-times-circle text-danger"></i>
                          <i v-else-if="step.status === 'pending'" class="fa fa-spinner fa-spin text-info"></i>
                          <i v-else class="fa fa-circle-o text-muted"></i>
                        </td>
                        <td>
                          <a class="make-cursor-pointer text-primary" style="font-size:11px;white-space:nowrap;"
                             @click.prevent="viewStep === (domain.id + key) ? viewStep = null : viewStep = (domain.id + key)">
                            {{ viewStep === (domain.id + key) ? 'Hide' : 'View' }}
                          </a>
                        </td>
                      </tr>
                      <tr v-if="viewStep === (domain.id + key)" :key="key + '-output'">
                        <td colspan="3" style="padding:0;">
                          <div style="background:#1e1e1e;border-radius:4px;padding:10px 14px;max-height:300px;overflow:auto;">
                            <pre style="font-size:11px;margin:0;white-space:pre-wrap;word-break:break-all;color:#d4d4d4;" v-html="colorizeJson(step.data)"></pre>
                          </div>
                        </td>
                      </tr>
                    </template>
                  </tbody>
                </table>
              </div>

            </div>
          </div>
        </div>

        <div v-else class="alert alert-warning" style="border-radius:8px;">
          <i class="fa fa-exclamation-triangle mr-2"></i> Batch not found.
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import {db} from '@/firebase/init';

export default {
  name: 'BatchDetail',
  data() {
    return {
      batchId: this.$route.params.batchId,
      batch: null,
      loading: true,
      expanded: {},
      copied: null,
      statusFilter: 'all',
      unsubscribe: null,
      domainSteps: {},
      stepsLoading: {},
      stepsListeners: {},
      viewStep: null
    };
  },
  computed: {
    headerStats() {
      const domains = (this.batch && this.batch.domains) || [];
      const count = s => domains.filter(d => {
        const st = d.status || 'pending';
        if (s === 'failed') return st === 'failed' || st === 'fail';
        if (s === 'in-progress') return st === 'in-progress' || st === 'inProgress' || st === 'running';
        return st === s;
      }).length;
      return [
        { label: 'Total',      value: domains.length,        color: '#333' },
        { label: 'Pending',    value: count('pending'),       color: '#856404' },
        { label: 'In Progress',value: count('in-progress'),   color: '#0c6674' },
        { label: 'Complete',   value: count('complete'),      color: '#28a745' },
        { label: 'Failed',     value: count('failed'),        color: '#dc3545' },
      ];
    },
    filteredDomains() {
      if (!this.batch || !this.batch.domains) return [];
      if (this.statusFilter === 'all') return this.batch.domains;
      return this.batch.domains.filter(d => {
        const s = d.status || 'pending';
        if (this.statusFilter === 'follow-up') return s === 'follow-up';
        if (this.statusFilter === 'failed') return s === 'failed' || s === 'fail';
        if (this.statusFilter === 'in-progress') return s === 'in-progress' || s === 'inProgress' || s === 'running';
        return s === this.statusFilter;
      });
    },
    filters() {
      const domains = (this.batch && this.batch.domains) || [];
      const count = s => domains.filter(d => {
        const st = d.status || 'pending';
        if (s === 'follow-up') return st === 'follow-up';
        if (s === 'failed') return st === 'failed' || st === 'fail';
        if (s === 'in-progress') return st === 'in-progress' || st === 'inProgress' || st === 'running';
        return st === s;
      }).length;
      const followUpCount = count('follow-up');
      const tabs = [
        { label: 'All', value: 'all', count: domains.length },
        { label: 'Pending', value: 'pending', count: count('pending') },
        { label: 'In Progress', value: 'in-progress', count: count('in-progress') },
        { label: 'Failed', value: 'failed', count: count('failed') },
        { label: 'Complete', value: 'completed', count: count('completed') },
      ];
      if (followUpCount) tabs.push({ label: 'Follow-up', value: 'follow-up', count: followUpCount });
      return tabs;
    },
    completedCount() {
      if (!this.batch || !this.batch.domains) return 0;
      return this.batch.domains.filter(d => {
        return d.status === 'pass' || d.status === 'complete' || d.status === 'completed';
      }).length;
    },
    progressPct() {
      if (!this.batch || !this.batch.totalCount) return 0;
      return Math.round((this.completedCount / this.batch.totalCount) * 100);
    }
  },
  mounted() {
    this.listenBatch();
  },
  beforeDestroy() {
    if (this.unsubscribe) this.unsubscribe();
    Object.values(this.stepsListeners).forEach(fn => fn());
  },
  methods: {
    listenBatch() {
      this.loading = true;
      this.unsubscribe = db.collection('cfEnterpriseBatches')
        .doc(this.batchId)
        .onSnapshot(doc => {
          if (!doc.exists) {
            this.batch = null;
            this.loading = false;
            return;
          }
          const data = doc.data();
          if (!this.batch) {
            // First load — set everything
            this.batch = { id: doc.id, ...data };
          } else {
            // Subsequent updates — merge to avoid re-mounting expanded rows
            this.batch.status = data.status;
            this.batch.totalCount = data.totalCount;
            // Merge domain updates in place by id
            const incoming = data.domains || [];
            const existing = this.batch.domains || [];
            const map = {};
            existing.forEach(d => { map[d.id] = d; });
            incoming.forEach(d => {
              if (map[d.id]) {
                const target = map[d.id];
                // Use $set for each key so Vue detects nested changes
                Object.keys(d).forEach(key => {
                  if (Array.isArray(d[key])) {
                    // Always replace arrays wholesale so Vue detects new items
                    this.$set(target, key, d[key]);
                  } else if (typeof d[key] === 'object' && d[key] !== null) {
                    if (!target[key]) {
                      this.$set(target, key, d[key]);
                    } else {
                      Object.keys(d[key]).forEach(subKey => {
                        this.$set(target[key], subKey, d[key][subKey]);
                      });
                    }
                  } else {
                    this.$set(target, key, d[key]);
                  }
                });
              } else {
                existing.push(d);
              }
            });
            if (!this.batch.domains) this.$set(this.batch, 'domains', incoming);
          }
          this.loading = false;
        }, err => {
          console.error('listenBatch error', err);
          this.loading = false;
        });
    },
    headerStatusClass(status) {
      const map = {
        pending: 'bg-warning text-dark',
        'in-progress': 'bg-info text-white', inProgress: 'bg-info text-white', running: 'bg-info text-white',
        complete: 'bg-success text-white', completed: 'bg-success text-white',
        failed: 'bg-danger text-white', fail: 'bg-danger text-white',
      };
      return map[status] || 'bg-secondary text-white';
    },
    headerStatusIcon(status) {
      if (status === 'pending') return 'fa-clock-o';
      if (status === 'in-progress' || status === 'inProgress' || status === 'running') return 'fa-spinner fa-spin';
      if (status === 'complete' || status === 'completed') return 'fa-check-circle';
      if (status === 'failed' || status === 'fail') return 'fa-times-circle';
      return 'fa-circle';
    },
    copyDomain(domain) {
      navigator.clipboard.writeText(domain.domain);
      this.copied = domain.id;
      setTimeout(() => { this.copied = null; }, 1500);
    },
    async toggle(domainId) {
      const opening = !this.expanded[domainId];
      this.$set(this.expanded, domainId, opening);
      if (opening && !this.domainSteps[domainId]) {
        await this.fetchSteps(domainId);
      }
    },
    fetchSteps(domainId) {
      if (this.stepsListeners[domainId]) return;
      this.$set(this.stepsLoading, domainId, true);
      const unsub = db.collection('domains').doc(domainId).onSnapshot(doc => {
        if (doc.exists) {
          const cf = doc.data().cfEnterprise || {};
          if (cf.steps) this.$set(this.domainSteps, domainId, cf.steps);
        }
        this.$set(this.stepsLoading, domainId, false);
      }, e => {
        console.error('fetchSteps error', e);
        this.$set(this.stepsLoading, domainId, false);
      });
      this.$set(this.stepsListeners, domainId, unsub);
    },
    precheckItems(prechecks) {
      // exclude status key, show it separately via badge
      const { status, ...rest } = prechecks;
      return rest;
    },
    precheckIcon(key, val) {
      if (key === 'detectedProxy') return val ? 'fa-shield text-warning' : 'fa-shield text-muted';
      if (val === true) return 'fa-check-circle text-success';
      if (val === false) return 'fa-times-circle text-danger';
      if (val === null || val === undefined) return 'fa-minus-circle text-muted';
      return 'fa-info-circle text-info';
    },
    precheckStyle(key, val) {
      if (key === 'detectedProxy') {
        return val
          ? { background: '#fff8e1', borderColor: '#ffe082' }
          : { background: '#f8f9fa', borderColor: '#dee2e6' };
      }
      if (val === true) return { background: '#f4fff8', borderColor: '#c3e6cb' };
      if (val === false) return { background: '#fff5f5', borderColor: '#f5c6cb' };
      return { background: '#f8f9fa', borderColor: '#dee2e6' };
    },
    precheckValueColor(key, val) {
      if (key === 'detectedProxy') return val ? '#856404' : '#666';
      if (val === true) return '#28a745';
      if (val === false) return '#dc3545';
      return '#888';
    },
    formatPrecheckValue(key, val) {
      if (val === null || val === undefined) return 'N/A';
      if (typeof val === 'boolean') return val ? 'Yes' : 'No';
      return val;
    },
    groupedLogs(logs) {
      return logs.reduce((acc, log) => {
        const key = log.type || 'general';
        if (!acc[key]) acc[key] = [];
        acc[key].push(log);
        return acc;
      }, {});
    },
    formatGroupName(name) {
      return name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    },
    sortedSteps(steps) {
      if (!steps) return {};
      return Object.keys(steps)
        .sort(function(a, b) {
          const seqA = (steps[a] && steps[a].seq != null) ? steps[a].seq : Infinity;
          const seqB = (steps[b] && steps[b].seq != null) ? steps[b].seq : Infinity;
          return seqA - seqB;
        })
        .reduce(function(acc, key) { acc[key] = steps[key]; return acc; }, {});
    },
    colorizeJson(data) {
      if (!data) return 'No data';
      const json = JSON.stringify(data, null, 2);
      return json.replace(
        /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*")(\s*:)?|true|false|null|-?\d+\.?\d*([eE][+-]?\d+)?/g,
        function(match, _str, _inner, colon) {
          if (colon) return '<span style="color:#6cb6ff;">' + match.slice(0, -colon.length) + '</span>' + colon;
          if (/^"/.test(match)) return '<span style="color:#ffb347;">' + match + '</span>';
          if (match === 'true') return '<span style="color:#85e89d;">true</span>';
          if (match === 'false') return '<span style="color:#f08080;">false</span>';
          if (match === 'null') return '<span style="color:#aaa;">null</span>';
          return '<span style="color:#ffb347;">' + match + '</span>';
        }
      );
    },
    formatTimestamp(val) {
      if (!val) return '';
      const d = new Date(val);
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    },
    stepIcon(status) {
      if (status === 'pass' || status === 'complete' || status === 'completed') return 'fa-check-circle text-success';
      if (status === 'fail' || status === 'failed') return 'fa-times-circle text-danger';
      if (status === 'running' || status === 'in-progress' || status === 'inProgress') return 'fa-spinner fa-spin text-info';
      return 'fa-circle-o text-muted';
    },
    statusBadge(status) {
      return {
        'badge-warning': status === 'pending',
        'badge-info': status === 'running' || status === 'in-progress' || status === 'inProgress',
        'badge-success': status === 'pass' || status === 'complete' || status === 'completed',
        'badge-danger': status === 'failed' || status === 'fail',
        'badge-warning': status === 'follow-up'
      };
    },
    statusLabel(status) {
      const map = {
        pending: 'Pending', running: 'Running', 'in-progress': 'In Progress',
        inProgress: 'In Progress', complete: 'Completed', completed: 'Completed', success: 'Completed',
        pass: 'Pass', failed: 'Failed', fail: 'Failed', 'follow-up': 'Follow-up'
      };
      return map[status] || status || '—';
    },
    formatDate(val) {
      if (!val) return '—';
      const d = val.toDate ? val.toDate() : new Date(val);
      return d.toLocaleString();
    }
  }
};
</script>

<style scoped>
.header-stat {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 10px 18px;
  min-width: 80px;
  text-align: center;
}
.header-stat-value {
  font-size: 20px;
  font-weight: 700;
  line-height: 1;
}
.header-stat-label {
  font-size: 11px;
  color: #999;
  text-transform: uppercase;
  letter-spacing: .5px;
  margin-top: 3px;
}

.filter-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border-radius: 7px;
  border: 1px solid transparent;
  background: transparent;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  color: #666;
  transition: all .15s ease;
}
.filter-btn:hover {
  background: #fff;
  border-color: #dee2e6;
  color: #333;
}
.filter-btn-count {
  background: #e9ecef;
  color: #666;
  border-radius: 99px;
  padding: 1px 7px;
  font-size: 11px;
  font-weight: 600;
  transition: all .15s ease;
}

/* Active states */
.filter-btn-active { background: #fff !important; border-color: #dee2e6 !important; color: #333 !important; box-shadow: 0 1px 3px rgba(0,0,0,0.08); }

.filter-btn-active.filter-btn-all .filter-btn-count       { background: #333; color: #fff; }
.filter-btn-active.filter-btn-pending .filter-btn-count   { background: #ffc107; color: #7a5c00; }
.filter-btn-active.filter-btn-in-progress .filter-btn-count { background: #17a2b8; color: #fff; }
.filter-btn-active.filter-btn-failed .filter-btn-count    { background: #dc3545; color: #fff; }
.filter-btn-active.filter-btn-complete .filter-btn-count  { background: #28a745; color: #fff; }

.filter-btn-active.filter-btn-pending   { color: #856404 !important; }
.filter-btn-active.filter-btn-in-progress { color: #0c6674 !important; }
.filter-btn-active.filter-btn-failed    { color: #721c24 !important; }
.filter-btn-active.filter-btn-complete  { color: #155724 !important; }

.section-label {
  font-size: 11px;
  font-weight: 700;
  color: #aaa;
  text-transform: uppercase;
  letter-spacing: .6px;
  margin-bottom: 10px;
}

.steps-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.step-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 9px 14px;
  border-radius: 7px;
  background: #fff;
  border: 1px solid #eee;
}

.step-row.step-pass, .step-row.step-complete, .step-row.step-completed {
  border-color: #c3e6cb;
  background: #f4fff8;
}

.step-row.step-fail, .step-row.step-failed {
  border-color: #f5c6cb;
  background: #fff5f5;
}

.step-row.step-running, .step-row.step-in-progress, .step-row.step-inProgress {
  border-color: #bee5eb;
  background: #f0fbfd;
}

.step-icon-wrap {
  width: 22px;
  flex-shrink: 0;
  font-size: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.log-terminal {
  background: #1a1d23;
  border-radius: 8px;
  padding: 14px 16px;
  max-height: 260px;
  overflow-y: auto;
  font-family: 'Courier New', monospace;
  font-size: 13px;
  line-height: 1.8;
}

.log-line {
  display: flex;
  gap: 10px;
}

.log-time {
  color: #6ab0f5;
  flex-shrink: 0;
  font-weight: 600;
}

.log-msg { color: #e8e8e8; font-weight: 600; }
.log-msg.log-error, .log-msg.log-failed, .log-msg.log-fail { color: #ff7b6b; font-weight: 700; }
.log-msg.log-success { color: #7ee787; font-weight: 700; }
.log-msg.log-warning { color: #f0c674; font-weight: 700; }

.log-type {
  flex-shrink: 0;
  font-weight: 700;
  font-size: 11px;
  opacity: .75;
}
.log-type-success { color: #89d185; }
.log-type-failed, .log-type-fail, .log-type-error { color: #f48771; }
.log-type-warning { color: #dcdcaa; }
.log-type-prechecks, .log-type-default { color: #9cdcfe; }

.empty-state {
  font-size: 13px;
  color: #aaa;
  padding: 6px 0;
}
</style>
